import type { PublishJobDto } from '@course-authoring/shared-types';
import { fetchWithAuthRetry } from './apiUtils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export interface PublishJobApiError {
  status: number;
  message: string;
  code?: string;
  publishJob?: PublishJobDto | null;
}

export function isPublishJobConflict(error: unknown): error is PublishJobApiError {
  return typeof error === 'object' && error !== null
    && (error as PublishJobApiError).status === 409
    && (error as PublishJobApiError).code === 'PUBLISH_JOB_CONFLICT';
}

async function toPublishJobApiError(response: Response, fallbackMessage: string): Promise<PublishJobApiError> {
  let message = fallbackMessage;
  let code: string | undefined;
  let publishJob: PublishJobDto | null | undefined;
  try {
    const payload: unknown = await response.json();
    if (typeof payload === 'object' && payload !== null) {
      const record = payload as Record<string, unknown>;
      if (typeof record.message === 'string') message = record.message;
      if (typeof record.code === 'string') code = record.code;
      if (record.publishJob === null || (typeof record.publishJob === 'object' && record.publishJob !== null)) {
        publishJob = record.publishJob as PublishJobDto | null;
      }
    }
  } catch {
    // Non-JSON error body — keep the fallback message.
  }
  return { status: response.status, message, code, publishJob };
}

export async function fetchPublishJob(id: number): Promise<PublishJobDto> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/publish-jobs/${id}`);
  if (!response.ok) throw await toPublishJobApiError(response, 'Failed to refresh preparation status');
  const payload = await response.json() as { publishJob: PublishJobDto };
  return payload.publishJob;
}

export async function retryPublishJob(id: number): Promise<PublishJobDto> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/publish-jobs/${id}/retry`, { method: 'POST' });
  if (!response.ok) throw await toPublishJobApiError(response, 'Failed to retry preparation');
  const payload = await response.json() as { publishJob: PublishJobDto };
  return payload.publishJob;
}
